import { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  ChevronDown,
  Circle,
  GitBranch,
  Webhook,
  ListChecks,
  PackageCheck,
  Shield,
  ShoppingBag,
  CreditCard,
  Globe,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  Plug,
  RotateCcw,
  ScanLine,
  Search,
  Settings,
  Sun,
  Users,
  X,
} from 'lucide-react';
import CommandPalette from '@/components/common/CommandPalette';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/authStore';
import { usePermissions } from '@/hooks/usePermissions';
import { useBillingPageData } from '@/hooks/useBilling';
import { useConfigReturnStatuses } from '@/hooks/useConfigReturnStatuses';
import { useReturnCountsByStatus } from '@/hooks/useAnfragen';
import { translateStatusTitle } from '@/components/anfragen/AnfragenFilters';
import { LanguageSwitcher } from '@/components/portal/LanguageSwitcher';

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const settingsSubItems = [
  { key: 'nav.globalSettings', href: '/settings/global', icon: Globe },
  { key: 'nav.workflows', href: '/settings/workflows', icon: GitBranch },
  { key: 'nav.webhooks', href: '/settings/webhooks', icon: Webhook },
  { key: 'nav.integrations', href: '/integrations', icon: Plug },
  { key: 'nav.portalSettings', href: '/portal/settings', icon: Settings },
];

const settingsPaths = settingsSubItems.map((i) => i.href);

/* Close DaisyUI dropdown after clicking a link inside it */
const closeDropdown = () => (document.activeElement as HTMLElement)?.blur();

/* ------------------------------------------------------------------ */
/*  Shared styles                                                      */
/* ------------------------------------------------------------------ */

const ICON = 'h-[18px] w-[18px]'; // consistent icon size everywhere
const LINK_BASE =
  'flex items-center gap-2 rounded-btn px-3 min-h-12 text-sm font-medium transition-colors duration-150';
const LINK_ACTIVE = 'bg-primary/10 text-primary';
const LINK_IDLE = 'text-base-content/70 hover:bg-base-300 hover:text-base-content';
const ICON_BTN =
  'flex items-center justify-center rounded-btn min-h-12 min-w-12 text-base-content/70 hover:bg-base-300 hover:text-base-content transition-colors duration-150';
const DROPDOWN_MENU =
  'dropdown-content menu z-[60] mt-1 w-56 rounded-card border border-go-border bg-base-200 p-2 shadow-lg [&_a]:min-h-0 [&_a]:h-auto [&_button]:min-h-0 [&_button]:h-auto';

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function TopNav() {
  const location = useLocation();
  const { t } = useTranslation();
  const { canReceiveWarehouseReturns } = usePermissions();
  const [searchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const { logout } = useAuth();
  const { data: statuses } = useConfigReturnStatuses();
  const { data: countsData } = useReturnCountsByStatus();
  const { data: billingData } = useBillingPageData();
  const isFreeAccount = billingData?.account?.status === 'free';

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [mobileReturnsOpen, setMobileReturnsOpen] = useState(false);
  const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false);
  const [isDark, setIsDark] = useState(
    document.documentElement.getAttribute('data-theme') === 'dark'
  );

  const toggleTheme = () => {
    const next = isDark ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', next);
    document.documentElement.style.colorScheme = next;
    localStorage.setItem('theme', next);
    setIsDark(!isDark);
  };

  // Global Ctrl+K / Cmd+K shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const isActive = (path: string) => location.pathname === path;
  const isReturnsActive = location.pathname === '/returns' || location.pathname.startsWith('/returns/');
  const isJobsActive = location.pathname === '/jobs' || location.pathname.startsWith('/jobs/');
  const isSettingsActive = settingsPaths.some((p) => location.pathname === p);
  const activeStatusId = searchParams.get('status_id');
  // Array.isArray-Guard statt `?? []`: TopNav liegt im Layout - eine transiente
  // Nicht-Array-Antwort (z.B. Objekt/HTML waehrend eines Deploy-Fensters) wuerde
  // sonst die komplette Admin-UI per Error-Boundary white-screenen.
  const menuStatuses = (Array.isArray(statuses) ? statuses : [])
    .filter((s) => s.show_in_menu)
    .slice(0, 10);

  const linkClass = (path: string) =>
    `${LINK_BASE} ${isActive(path) ? LINK_ACTIVE : LINK_IDLE}`;

  const initials =
    [user?.first_name?.[0], user?.last_name?.[0]]
      .filter(Boolean)
      .join('')
      .toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U';

  const displayName =
    [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || '';


  const closeMobileNav = () => setDrawerOpen(false);

  /* ---- Avatar helper (reused desktop + mobile) ---- */
  const Avatar = ({ size = 24 }: { size?: number }) => (
    <div
      className="shrink-0 rounded-full overflow-hidden"
      style={{ width: size, height: size }}
    >
      {user?.avatar_base64 ? (
        <img src={user.avatar_base64} alt="" className="h-full w-full object-cover" />
      ) : (
        <div className="h-full w-full bg-primary text-primary-content flex items-center justify-center">
          <span style={{ fontSize: size * 0.45 }} className="font-semibold leading-none">
            {initials}
          </span>
        </div>
      )}
    </div>
  );

  /* ================================================================ */
  /*  Render                                                           */
  /* ================================================================ */

  return (
    <>
      {/* ============================================================ */}
      {/*  NAV BAR                                                      */}
      {/* ============================================================ */}
      <nav className="sticky top-0 z-50 h-16 border-b border-base-300 bg-base-200 px-4 flex items-center">

        {/* --- Left: Logo + Nav --- */}
        <div className="hidden lg:flex items-center gap-2">
          {/* Logo */}
          <Link
            to="/dashboard"
            className="shrink-0 mr-1 h-12 overflow-hidden flex items-center hover:opacity-80 transition-opacity"
          >
            <img src={`${import.meta.env.BASE_URL}goe-returns-logo-white.png`} alt="goe-returns" className="h-12 w-auto logo-dark" />
            <img src={`${import.meta.env.BASE_URL}goe-returns-logo-black.png`} alt="goe-returns" className="h-12 w-auto logo-light" />
          </Link>

          <Link to="/dashboard" className={linkClass('/dashboard')}>
            <LayoutDashboard className={ICON} />
            {t('nav.dashboard')}
          </Link>

          <Link to="/deliveries" className={linkClass('/deliveries')}>
            <PackageCheck className={ICON} />
            {t('nav.deliveries')}
          </Link>

          <Link to="/products" className={linkClass('/products')}>
            <ShoppingBag className={ICON} />
            {t('nav.products')}
          </Link>

          {canReceiveWarehouseReturns && (
            <Link to="/warehouse/quick-receipt" className={linkClass('/warehouse/quick-receipt')}>
              <ScanLine className={ICON} />
              {t('nav.warehouse')}
            </Link>
          )}

          {/* Returns dropdown */}
          <div className="dropdown">
            <button
              tabIndex={0}
              className={`${LINK_BASE} ${isReturnsActive ? LINK_ACTIVE : LINK_IDLE}`}
            >
              <RotateCcw className={ICON} />
              {t('nav.returns')}
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
            <ul tabIndex={0} className={`${DROPDOWN_MENU} w-64`}>
              {/* All returns */}
              <li>
                <Link
                  to="/returns"
                  className={isActive('/returns') && !activeStatusId ? 'bg-primary/10 text-primary' : ''}
                  onClick={closeDropdown}
                >
                  <RotateCcw className={ICON} />
                  {t('nav.allReturns')}
                </Link>
              </li>
              {menuStatuses.length > 0 && (
                <li className="py-1"><div className="border-t border-base-300" /></li>
              )}
              {/* Per-status items (max 10, only show_in_menu) */}
              {menuStatuses.map((s) => {
                const count = countsData?.counts?.[String(s.id)] ?? 0;
                const isStatusActive = isActive('/returns') && activeStatusId === String(s.id);
                return (
                  <li key={s.id}>
                    <Link
                      to={`/returns?status_id=${s.id}`}
                      className={isStatusActive ? 'bg-primary/10 text-primary' : ''}
                      onClick={closeDropdown}
                    >
                      <Circle
                        className="h-3 w-3 shrink-0"
                        fill={s.color}
                        stroke={s.color}
                      />
                      <span className="truncate">{translateStatusTitle(s.title, t)}</span>
                      {count > 0 && (
                        <span className="ml-auto badge badge-sm badge-ghost">{count}</span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Settings dropdown */}
          <div className="dropdown">
            <button
              tabIndex={0}
              className={`${LINK_BASE} ${isSettingsActive ? LINK_ACTIVE : LINK_IDLE}`}
            >
              <Settings className={ICON} />
              {t('nav.settings')}
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
            <ul tabIndex={0} className={DROPDOWN_MENU}>
              {settingsSubItems.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.key}>
                    <Link
                      to={item.href}
                      className={isActive(item.href) ? 'bg-primary/10 text-primary' : ''}
                      onClick={closeDropdown}
                    >
                      <Icon className={ICON} />
                      {t(item.key)}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Mobile: Logo only */}
        <Link
          to="/dashboard"
          className="shrink-0 h-12 overflow-hidden flex items-center hover:opacity-80 transition-opacity lg:hidden"
        >
          <img src={`${import.meta.env.BASE_URL}goe-returns-logo-white.png`} alt="goe-returns" className="h-12 w-auto logo-dark" />
          <img src={`${import.meta.env.BASE_URL}goe-returns-logo-black.png`} alt="goe-returns" className="h-12 w-auto logo-light" />
        </Link>

        {/* --- Spacer --- */}
        <div className="flex-1 min-w-0" />

        {/* --- Right: Search + Actions + Profile --- */}
        <div className="hidden lg:flex items-center gap-2">
          {/* Admin button - only for @go-ecommerce.de */}
          {user?.email?.endsWith('@go-ecommerce.de') && (
            <Link
              to="/admin"
              className={`${ICON_BTN} ${location.pathname.startsWith('/admin') ? 'text-primary' : ''}`}
              title={t('admin.adminPanel')}
            >
              <Shield className={ICON} />
            </Link>
          )}

          {/* Search */}
          {commandPaletteOpen ? (
            <CommandPalette
              isOpen={commandPaletteOpen}
              onClose={() => setCommandPaletteOpen(false)}
            />
          ) : (
            <button
              onClick={() => setCommandPaletteOpen(true)}
              className="flex items-center gap-2 min-h-12 w-72 rounded-btn border border-base-300 bg-base-100 px-3 text-sm text-base-content/40 hover:border-primary transition-colors cursor-pointer"
            >
              <Search className="h-4 w-4 shrink-0" />
              <span className="flex-1 text-left">{t('search.placeholder')}</span>
              <kbd className="kbd kbd-sm text-base-content/30">Ctrl+K</kbd>
            </button>
          )}

          {/* Theme toggle */}
          <button className={ICON_BTN} onClick={toggleTheme} aria-label={t('header.toggleTheme')}>
            {isDark ? <Sun className={ICON} /> : <Moon className={ICON} />}
          </button>

          {/* Language */}
          <LanguageSwitcher />

          {/* Profile dropdown */}
          <div className="dropdown dropdown-end">
            <button
              tabIndex={0}
              className={`${LINK_BASE} ${LINK_IDLE}`}
            >
              <Avatar size={24} />
              <span className="max-w-[120px] truncate">{displayName}</span>
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
            <ul tabIndex={0} className={`${DROPDOWN_MENU} w-52`}>
              <li className="menu-title"><span>{displayName}</span></li>
              <li>
                <Link to="/profile" onClick={closeDropdown}>
                  <Building2 className={ICON} />{t('nav.profile')}
                </Link>
              </li>
              <li>
                <Link to="/team" onClick={closeDropdown}>
                  <Users className={ICON} />{t('nav.team')}
                </Link>
              </li>
              {canReceiveWarehouseReturns && (
                <li>
                  <Link to="/jobs" onClick={closeDropdown} className={isJobsActive ? 'active' : ''}>
                    <ListChecks className={ICON} />{t('nav.jobs')}
                  </Link>
                </li>
              )}
              {!isFreeAccount && (
                <li>
                  <Link to="/subscription" onClick={closeDropdown}>
                    <CreditCard className={ICON} />{t('nav.subscription')}
                  </Link>
                </li>
              )}
              <li><div className="my-1 border-t border-base-300" /></li>
              <li>
                <a onClick={() => { closeDropdown(); logout(); }}>
                  <LogOut className={ICON} />{t('auth.logout')}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* --- Mobile: search + hamburger --- */}
        <button
          className={`${ICON_BTN} lg:hidden`}
          onClick={() => setCommandPaletteOpen(true)}
          aria-label={t('search.openShortcut')}
        >
          <Search className="h-5 w-5" />
        </button>
        <button
          className={`${ICON_BTN} lg:hidden`}
          onClick={() => setDrawerOpen(true)}
          aria-label="Menu"
        >
          <Menu className="h-6 w-6" />
        </button>
      </nav>

      {/* Mobile fullscreen search overlay (single instance, no desktop duplicate) */}
      {commandPaletteOpen && (
        <div className="fixed inset-0 z-50 bg-base-100 p-4 lg:hidden">
          <CommandPalette
            isOpen={commandPaletteOpen}
            onClose={() => setCommandPaletteOpen(false)}
          />
        </div>
      )}

      {/* ============================================================ */}
      {/*  MOBILE DRAWER                                                */}
      {/* ============================================================ */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" onClick={closeMobileNav}>
          <div className="absolute inset-0 bg-black/50" />

          <aside
            className="absolute right-0 top-0 h-full w-72 bg-base-200 shadow-xl animate-slide-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex h-full flex-col">
              {/* Header */}
              <div className="flex h-16 items-center justify-between border-b border-base-300 px-4">
                <span className="text-lg font-semibold">{t('nav.settings')}</span>
                <button className={ICON_BTN} onClick={closeMobileNav}>
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Nav items */}
              <nav className="flex-1 overflow-y-auto px-3 py-4">
                <ul className="space-y-1">
                  <li>
                    <Link to="/dashboard" className={linkClass('/dashboard')} onClick={closeMobileNav}>
                      <LayoutDashboard className={ICON} /> {t('nav.dashboard')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/deliveries" className={linkClass('/deliveries')} onClick={closeMobileNav}>
                      <PackageCheck className={ICON} /> {t('nav.deliveries')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/products" className={linkClass('/products')} onClick={closeMobileNav}>
                      <ShoppingBag className={ICON} /> {t('nav.products')}
                    </Link>
                  </li>
                  {canReceiveWarehouseReturns && (
                    <li>
                      <Link
                        to="/warehouse/quick-receipt"
                        className={linkClass('/warehouse/quick-receipt')}
                        onClick={closeMobileNav}
                      >
                        <ScanLine className={ICON} /> {t('nav.warehouse')}
                      </Link>
                    </li>
                  )}
                  {/* Returns accordion */}
                  <li>
                    <button
                      className={`${LINK_BASE} w-full justify-between ${isReturnsActive ? LINK_ACTIVE : LINK_IDLE}`}
                      onClick={() => setMobileReturnsOpen(!mobileReturnsOpen)}
                    >
                      <span className="flex items-center gap-2">
                        <RotateCcw className={ICON} /> {t('nav.returns')}
                      </span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${mobileReturnsOpen ? 'rotate-180' : ''}`} />
                    </button>
                    <ul className={`mt-1 space-y-1 overflow-hidden transition-all duration-200 ${mobileReturnsOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                      <li>
                        <Link
                          to="/returns"
                          className={`${LINK_BASE} pl-10 ${isActive('/returns') && !activeStatusId ? LINK_ACTIVE : LINK_IDLE}`}
                          onClick={closeMobileNav}
                        >
                          <RotateCcw className={ICON} /> {t('nav.allReturns')}
                        </Link>
                      </li>
                      {menuStatuses.map((s) => {
                        const count = countsData?.counts?.[String(s.id)] ?? 0;
                        const isStatusActive = isActive('/returns') && activeStatusId === String(s.id);
                        return (
                          <li key={s.id}>
                            <Link
                              to={`/returns?status_id=${s.id}`}
                              className={`${LINK_BASE} pl-10 ${isStatusActive ? LINK_ACTIVE : LINK_IDLE}`}
                              onClick={closeMobileNav}
                            >
                              <Circle
                                className="h-3 w-3 shrink-0"
                                fill={s.color}
                                stroke={s.color}
                              />
                              <span className="truncate">{translateStatusTitle(s.title, t)}</span>
                              {count > 0 && (
                                <span className="ml-auto badge badge-sm badge-ghost">{count}</span>
                              )}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </li>

                  {/* Admin link - only for @go-ecommerce.de */}
                  {user?.email?.endsWith('@go-ecommerce.de') && (
                    <li>
                      <Link to="/admin" className={`${LINK_BASE} ${location.pathname.startsWith('/admin') ? LINK_ACTIVE : LINK_IDLE}`} onClick={closeMobileNav}>
                        <Shield className={ICON} /> {t('admin.adminPanel')}
                      </Link>
                    </li>
                  )}

                  <li className="py-2"><div className="border-t border-base-300" /></li>

                  {/* Settings accordion */}
                  <li>
                    <button
                      className={`${LINK_BASE} w-full justify-between ${isSettingsActive ? LINK_ACTIVE : LINK_IDLE}`}
                      onClick={() => setMobileSettingsOpen(!mobileSettingsOpen)}
                    >
                      <span className="flex items-center gap-2">
                        <Settings className={ICON} /> {t('nav.settings')}
                      </span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${mobileSettingsOpen ? 'rotate-180' : ''}`} />
                    </button>
                    <ul className={`mt-1 space-y-1 overflow-hidden transition-all duration-200 ${mobileSettingsOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                      {settingsSubItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <li key={item.key}>
                            <Link
                              to={item.href}
                              className={`${LINK_BASE} pl-10 ${isActive(item.href) ? LINK_ACTIVE : LINK_IDLE}`}
                              onClick={closeMobileNav}
                            >
                              <Icon className={ICON} /> {t(item.key)}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </li>

                  <li className="py-2"><div className="border-t border-base-300" /></li>

                  <li>
                    <button className={`${LINK_BASE} w-full ${LINK_IDLE}`} onClick={toggleTheme}>
                      {isDark ? <Sun className={ICON} /> : <Moon className={ICON} />}
                      {t('header.toggleTheme')}
                    </button>
                  </li>
                  <li className="px-3 py-2">
                    <LanguageSwitcher />
                  </li>
                </ul>
              </nav>

              {/* Footer: profile + logout */}
              <div className="border-t border-base-300 p-3">
                <div className="flex items-center gap-3 px-3 py-2">
                  <Avatar size={36} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{displayName}</p>
                    <p className="text-xs text-base-content/50 truncate">{user?.email}</p>
                  </div>
                </div>
                <ul className="mt-2 space-y-1">
                  <li>
                    <Link to="/profile" className={`${LINK_BASE} w-full ${isActive('/profile') ? LINK_ACTIVE : LINK_IDLE}`} onClick={closeMobileNav}>
                      <Building2 className={ICON} /> {t('nav.profile')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/team" className={`${LINK_BASE} w-full ${isActive('/team') ? LINK_ACTIVE : LINK_IDLE}`} onClick={closeMobileNav}>
                      <Users className={ICON} /> {t('nav.team')}
                    </Link>
                  </li>
                  {canReceiveWarehouseReturns && (
                    <li>
                      <Link to="/jobs" className={`${LINK_BASE} w-full ${isJobsActive ? LINK_ACTIVE : LINK_IDLE}`} onClick={closeMobileNav}>
                        <ListChecks className={ICON} /> {t('nav.jobs')}
                      </Link>
                    </li>
                  )}
                  {!isFreeAccount && (
                    <li>
                      <Link to="/subscription" className={`${LINK_BASE} w-full ${isActive('/subscription') ? LINK_ACTIVE : LINK_IDLE}`} onClick={closeMobileNav}>
                        <CreditCard className={ICON} /> {t('nav.subscription')}
                      </Link>
                    </li>
                  )}
                </ul>
                <button
                  className={`${LINK_BASE} w-full mt-2 text-error hover:bg-error/10`}
                  onClick={() => { closeMobileNav(); logout(); }}
                >
                  <LogOut className={ICON} /> {t('auth.logout')}
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
